import { supabase } from '@/lib/supabase/client';
import { normalizeEmail } from '@/lib/contact-vault/normalize';
import type { LeadIntakeQueueInsert, LeadIntakeQueueRow } from '@/types/lead-intake';

function domainFromEmail(email: string): string | null {
  const domain = email.split('@')[1]?.trim().toLowerCase();
  return domain ? `https://${domain}` : null;
}

export const leadIntakeService = {
  async listRecent(limit = 20): Promise<LeadIntakeQueueRow[]> {
    const { data, error } = await supabase
      .from('lead_intake_queue')
      .select('*')
      .order('requested_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error listing lead intake queue:', error.message);
      throw new Error(`Failed to list intake queue: ${error.message}`);
    }

    return (data as LeadIntakeQueueRow[]) || [];
  },

  async getById(id: string): Promise<LeadIntakeQueueRow | null> {
    const { data, error } = await supabase.from('lead_intake_queue').select('*').eq('id', id).maybeSingle();

    if (error) {
      console.error('Error fetching intake queue row:', error.message);
      throw new Error(`Failed to fetch intake queue row: ${error.message}`);
    }

    return (data as LeadIntakeQueueRow) || null;
  },

  async queueContact(input: LeadIntakeQueueInsert, requestedBy?: string | null): Promise<LeadIntakeQueueRow> {
    const email_norm = normalizeEmail(input.email);
    if (!email_norm) {
      throw new Error('A valid email address is required.');
    }

    const { data: existingPending } = await supabase
      .from('lead_intake_queue')
      .select('id, status')
      .eq('email_norm', email_norm)
      .in('status', ['pending', 'processing'])
      .maybeSingle();

    if (existingPending?.id) {
      throw new Error('This email is already queued for research.');
    }

    const { data, error } = await supabase
      .from('lead_intake_queue')
      .insert({
        contact_person: input.contact_person?.trim() || null,
        email: input.email.trim(),
        email_norm,
        state_hint: input.state_hint?.trim().toUpperCase().slice(0, 2) || null,
        notes: input.notes?.trim() || null,
        website_hint: input.website_hint?.trim() || domainFromEmail(email_norm),
        requested_by: requestedBy ?? null,
        status: 'pending',
      })
      .select('*')
      .single();

    if (error) {
      console.error('Error queueing lead intake:', error.message);
      throw new Error(`Failed to queue contact for research: ${error.message}`);
    }

    return data as LeadIntakeQueueRow;
  },

  async queueContacts(
    inputs: LeadIntakeQueueInsert[],
    requestedBy?: string | null
  ): Promise<{ queued: LeadIntakeQueueRow[]; skipped: { email: string; reason: string }[] }> {
    const queued: LeadIntakeQueueRow[] = [];
    const skipped: { email: string; reason: string }[] = [];

    for (const input of inputs) {
      try {
        const row = await this.queueContact(input, requestedBy);
        queued.push(row);
      } catch (error) {
        skipped.push({
          email: input.email,
          reason: error instanceof Error ? error.message : 'Failed to queue contact',
        });
      }
    }

    return { queued, skipped };
  },
};
